import { useEffect, useMemo, useRef, useState } from 'react'
import type { ActivityPoint } from '~/lib/types'

const PAD = { top: 16, right: 16, bottom: 26, left: 34 }
const HEIGHT = 220

const SERIES = [
  { key: 'outbound' as const, label: 'Sent by agent', color: 'var(--tk-brand)' },
  { key: 'inbound' as const, label: 'Student replies', color: 'var(--tk-series-2)' },
]

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

/*
  Two series, so a legend is always present and both lines are direct-labeled
  at their last point. Hover gives a crosshair with both values at that day.
*/
export default function ActivityChart({ data }: { data: ActivityPoint[] }) {
  const wrapper = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(720)
  const [hover, setHover] = useState<number | null>(null)

  useEffect(() => {
    const node = wrapper.current
    if (!node) return

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const geometry = useMemo(() => {
    const innerW = Math.max(width - PAD.left - PAD.right, 10)
    const innerH = HEIGHT - PAD.top - PAD.bottom
    const max = Math.max(1, ...data.flatMap((point) => [point.outbound, point.inbound]))
    const step = data.length > 1 ? innerW / (data.length - 1) : 0

    const x = (index: number) => PAD.left + index * step
    const y = (value: number) => PAD.top + innerH - (value / max) * innerH

    const line = (key: 'outbound' | 'inbound') =>
      data.map((point, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(point[key])}`).join(' ')

    return { x, y, line, max, innerH, ticks: [0, Math.round(max / 2), max] }
  }, [data, width])

  const track = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const innerW = Math.max(width - PAD.left - PAD.right, 10)
    const ratio = (event.clientX - rect.left - PAD.left) / innerW
    const index = Math.round(ratio * (data.length - 1))
    setHover(Math.min(data.length - 1, Math.max(0, index)))
  }

  const active = hover === null ? null : data[hover]
  const last = data[data.length - 1]

  return (
    <div ref={wrapper} className="relative">
      <ul className="mb-2 flex flex-wrap gap-4 text-xs text-ink-2">
        {SERIES.map((series) => (
          <li key={series.key} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ backgroundColor: series.color }}
            />
            {series.label}
          </li>
        ))}
      </ul>

      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label={`Messages sent by the agent and replies from students over the last ${data.length} days`}
        onPointerMove={track}
        onPointerLeave={() => setHover(null)}
        className="touch-none"
      >
        {geometry.ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={geometry.y(tick)}
              y2={geometry.y(tick)}
              stroke="var(--tk-grid)"
              strokeWidth="1"
            />
            <text
              x={PAD.left - 8}
              y={geometry.y(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-faint text-[10px] [font-variant-numeric:tabular-nums]"
            >
              {tick}
            </text>
          </g>
        ))}

        {SERIES.map((series) => (
          <g key={series.key}>
            <path
              d={geometry.line(series.key)}
              fill="none"
              stroke={series.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx={geometry.x(data.length - 1)}
              cy={geometry.y(last[series.key])}
              r="4"
              fill={series.color}
              stroke="var(--tk-surface)"
              strokeWidth="2"
            />
          </g>
        ))}

        {active && hover !== null && (
          <g>
            <line
              x1={geometry.x(hover)}
              x2={geometry.x(hover)}
              y1={PAD.top}
              y2={PAD.top + geometry.innerH}
              stroke="var(--tk-grid)"
              strokeWidth="1"
            />
            {SERIES.map((series) => (
              <circle
                key={series.key}
                cx={geometry.x(hover)}
                cy={geometry.y(active[series.key])}
                r="5"
                fill={series.color}
                stroke="var(--tk-surface)"
                strokeWidth="2"
              />
            ))}
          </g>
        )}

        {data.map((point, i) =>
          i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2) ? (
            <text
              key={point.date}
              x={geometry.x(i)}
              y={HEIGHT - 6}
              textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
              className="fill-faint text-[10px]"
            >
              {shortDate(point.date)}
            </text>
          ) : null,
        )}
      </svg>

      {active && hover !== null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-lg"
          style={{ left: geometry.x(hover), top: 8 }}
        >
          <p className="text-faint">{shortDate(active.date)}</p>
          {SERIES.map((series) => (
            <p key={series.key} className="mt-0.5 flex items-center gap-1.5 whitespace-nowrap">
              <span
                aria-hidden="true"
                className="size-2 rounded-full"
                style={{ backgroundColor: series.color }}
              />
              <span className="text-ink-2">{series.label}</span>
              <span className="font-medium [font-variant-numeric:tabular-nums]">
                {active[series.key]}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
